<template>
  <div class="payment-polling-indicator bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
    <!-- Spinner and Status -->
    <div class="flex items-center gap-3">
      <!-- Animated Spinner -->
      <div class="relative w-12 h-12">
        <svg class="animate-spin w-full h-full text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <!-- Pulse Effect -->
        <div class="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-75"></div>
      </div>

      <!-- Status Message -->
      <div class="flex-1">
        <div class="font-semibold text-blue-900">
          {{ statusMessage }}
        </div>
        <div class="text-sm text-blue-700 mt-0.5">
          {{ subMessage }}
        </div>
      </div>
    </div>

    <!-- Progress Bar -->
    <div class="space-y-2">
      <div class="flex justify-between text-xs text-blue-700">
        <span>Progression</span>
        <span>{{ attempts }} / {{ maxAttempts }}</span>
      </div>
      <div class="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
        <div
          class="bg-blue-600 h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden"
          :style="{ width: progressPercentage + '%' }"
        >
          <!-- Animated shimmer effect -->
          <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-shimmer"></div>
        </div>
      </div>
      <div class="text-xs text-blue-600 text-center">
        {{ progressPercentage }}% complété
      </div>
    </div>

    <!-- Estimated Time -->
    <div v-if="estimatedTimeRemaining > 0" class="bg-blue-100 rounded-lg p-3">
      <div class="flex items-center gap-2">
        <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-sm text-blue-800">
          Temps estimé: {{ formatTime(estimatedTimeRemaining) }}
        </span>
      </div>
    </div>

    <!-- Step Indicators -->
    <div class="flex justify-between items-center pt-2">
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="flex flex-col items-center gap-1"
        :class="{ 'opacity-50': index > currentStep }"
      >
        <div
          class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors"
          :class="getStepClass(index)"
        >
          <span v-if="getStepStatus(index) === 'completed'">✓</span>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <span class="text-xs text-blue-700 max-w-16 text-center">{{ step }}</span>
      </div>
    </div>

    <!-- Warning for Long Wait -->
    <div v-if="attempts > 20" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
      <div class="flex items-start gap-2">
        <svg class="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <div class="flex-1">
          <div class="text-sm font-medium text-yellow-800">Traitement plus long que prévu</div>
          <div class="text-xs text-yellow-700 mt-1">
            Le paiement prend plus de temps que normal. Ne fermez pas cette page.
          </div>
        </div>
      </div>
    </div>

    <!-- Success State -->
    <div v-if="status === 'completed'" class="bg-green-50 border border-green-200 rounded-lg p-3">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <span class="text-sm font-medium text-green-800">Paiement réussi !</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="status === 'failed'" class="bg-red-50 border border-red-200 rounded-lg p-3">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <span class="text-sm font-medium text-red-800">Échec du paiement</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentPollingIndicator',

  props: {
    attempts: {
      type: Number,
      default: 0
    },
    maxAttempts: {
      type: Number,
      default: 30
    },
    status: {
      type: String,
      default: 'pending' // 'pending', 'processing', 'completed', 'failed'
    }
  },

  data() {
    return {
      steps: [
        'Initialisation',
        'Confirmation',
        'Traitement',
        'Finalisation'
      ],
      pollingInterval: 2000 // 2 seconds per attempt
    };
  },

  computed: {
    progressPercentage() {
      return Math.min(100, Math.round((this.attempts / this.maxAttempts) * 100));
    },

    currentStep() {
      const stepProgress = this.attempts / this.maxAttempts;
      if (stepProgress < 0.25) return 0;
      if (stepProgress < 0.5) return 1;
      if (stepProgress < 0.75) return 2;
      return 3;
    },

    estimatedTimeRemaining() {
      const remainingAttempts = this.maxAttempts - this.attempts;
      return remainingAttempts * this.pollingInterval;
    },

    statusMessage() {
      switch (this.status) {
        case 'pending':
          return 'En attente de confirmation';
        case 'processing':
          return 'Traitement du paiement';
        case 'completed':
          return 'Paiement terminé';
        case 'failed':
          return 'Paiement échoué';
        default:
          return 'Traitement en cours';
      }
    },

    subMessage() {
      if (this.status === 'completed') return 'Votre transaction a été validée';
      if (this.status === 'failed') return 'Une erreur est survenue';

      if (this.attempts < 5) return 'Vérification en cours...';
      if (this.attempts < 15) return 'Confirmation du paiement...';
      if (this.attempts < 25) return 'Finalisation de la transaction...';
      return 'Presque terminé...';
    }
  },

  methods: {
    getStepClass(index) {
      const status = this.getStepStatus(index);
      switch (status) {
        case 'completed':
          return 'bg-green-500 text-white';
        case 'current':
          return 'bg-blue-500 text-white ring-4 ring-blue-200';
        default:
          return 'bg-blue-200 text-blue-700';
      }
    },

    getStepStatus(index) {
      if (this.status === 'completed') return 'completed';
      if (this.status === 'failed' && index > this.currentStep) return 'pending';

      if (index < this.currentStep) return 'completed';
      if (index === this.currentStep) return 'current';
      return 'pending';
    },

    formatTime(milliseconds) {
      const seconds = Math.ceil(milliseconds / 1000);

      if (seconds < 60) {
        return `${seconds} seconde${seconds > 1 ? 's' : ''}`;
      }

      const minutes = Math.ceil(seconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  }
};
</script>

<style scoped>
.payment-polling-indicator {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 1.5s infinite;
}
</style>
